import { Injectable, inject } from "@angular/core";
import { ApiResource } from "@auvious/common";

import { DeepfakeFailureReasonEnum } from "../core-ui.enums";
import type { DeepfakeTrigger } from "../models";
import { ApplicationService } from "./application.service";
import type { IDeepfakeEventPayload } from "./deepfake.service";
import { AuviousRtcService } from "./rtc.service";

/**
 * One check as the ai records it: what `GET .../deepfake/:taskId` answers and
 * what `GET .../deepfake?conferenceId=` lists. `event` and `payload` are the
 * check's latest MQTT event (the payload byte-identical to it); the rest is
 * known to the ai only, never sent in an event.
 */
export interface IDeepfakeTaskState {
  taskId: string;
  event: "processing" | "created" | "failed";
  payload?: IDeepfakeEventPayload["payload"];
  /** The user id of the agent whose request started the check. */
  createdBy?: string;
  trigger?: DeepfakeTrigger;
  /** ISO timestamp of the request. */
  createdAt?: string;
  conferenceId?: string;
}

/** What `GET .../deepfake?conferenceId=` answers: newest first, at most 100. */
export interface IDeepfakeCheckList {
  items: IDeepfakeTaskState[];
}

/**
 * What the POST answers. `existing` is set when an automatic (`policy`)
 * check of the same stream in the same conference already exists: `id` is
 * that check, no scan was started, and no events will come for it here.
 */
export interface IDeepfakeSubmitResult {
  id: string;
  existing?: boolean;
}

/**
 * The media a check captured, one file part each (all optional, each at most
 * once): the still, an audio-only clip, a video clip.
 */
export type DeepfakeMediaParts = Partial<
  Record<"image" | "audio" | "video", Blob>
>;

/** The text fields that go with the media: who, where, and what asked. */
export type DeepfakeDetectionFields = Record<string, string | undefined>;

/**
 * The file name a part is sent with — informational only (the part's
 * Content-Type is the Blob's own type), e.g. `image.png`, `video.webm`.
 */
function fileNameFor(part: string, blob: Blob): string {
  const subtype = blob.type.split(";")[0].split("/")[1]?.trim();
  const extension = subtype === "jpeg" ? "jpg" : subtype;

  return extension ? `${part}.${extension}` : part;
}

/**
 * The multipart body of a detection request. The Blobs go as they are, so
 * each file part carries the type the browser actually produced; the text
 * fields go as strings, and an undefined one is left out rather than sent as
 * the word "undefined". Text first, so the server can refuse bad fields
 * before reading the media.
 */
export function detectionForm(
  media: DeepfakeMediaParts,
  fields: DeepfakeDetectionFields
): FormData {
  const form = new FormData();

  for (const [name, value] of Object.entries(fields)) {
    if (value !== undefined && value !== null) form.append(name, String(value));
  }

  for (const [part, blob] of Object.entries(media)) {
    if (blob) form.append(part, blob, fileNameFor(part, blob));
  }

  return form;
}

/**
 * A draining ai pod (graceful shutdown) refuses new work with a 503 carrying
 * this `errorCode` and a `Retry-After`; the next attempt reaches another pod
 * through the load balancer. Not to be confused with the 503 of an
 * application that has no Reality Defender key, a state rather than a blip.
 */
export const SERVICE_DRAINING_ERROR_CODE = "SERVICE_DRAINING";
/** The wait before retrying a draining 503 that names none (the ai sends 5 s). */
export const DEEPFAKE_DRAIN_RETRY_DEFAULT_MS = 5_000;
/** The longest a `Retry-After` is honoured for: an agent is waiting. */
export const DEEPFAKE_DRAIN_RETRY_MAX_MS = 30_000;
/** POSTs in all, the first one included, before a draining 503 is shown. */
export const DEEPFAKE_DRAIN_MAX_ATTEMPTS = 3;

/** What an error from the shared client (axios underneath) carries, if any. */
interface IHttpErrorLike {
  status?: number;
  statusCode?: number;
  response?: {
    status?: number;
    data?: { errorCode?: string } | null;
    headers?: Record<string, unknown> & { get?: (name: string) => unknown };
  };
}

function statusOf(ex: unknown): number | undefined {
  const error = ex as IHttpErrorLike;

  return error?.status ?? error?.statusCode ?? error?.response?.status;
}

function errorCodeOf(ex: unknown): string | undefined {
  const code = (ex as IHttpErrorLike)?.response?.data?.errorCode;

  return typeof code === "string" ? code : undefined;
}

/** The raw `Retry-After` header of a response, whatever the headers object. */
function retryAfterOf(ex: unknown): string | undefined {
  const headers = (ex as IHttpErrorLike)?.response?.headers;

  if (!headers) return undefined;

  const value =
    (typeof headers.get === "function" ? headers.get("retry-after") : null) ??
    headers["retry-after"] ??
    headers["Retry-After"];

  return value === undefined || value === null || value === ""
    ? undefined
    : String(value);
}

/**
 * Whether an error is a 503 worth retrying: a pod draining (its `errorCode`),
 * or — from a proxy that keeps the header but not the body — any 503 that
 * says when to come back and does not name the missing key. The 503 of an
 * application without a key has neither.
 */
function isTransient503(ex: unknown): boolean {
  if (statusOf(ex) !== 503) return false;

  const code = errorCodeOf(ex);

  if (code === SERVICE_DRAINING_ERROR_CODE) return true;
  if (code === DeepfakeFailureReasonEnum.notConfigured) return false;

  return retryAfterOf(ex) !== undefined;
}

/**
 * How long to wait before retrying a request that met a draining pod, in ms;
 * `undefined` when the error is not one to retry. `Retry-After` is seconds or
 * an HTTP date; anything else is the default, and the wait is capped.
 */
export function drainRetryDelayMs(ex: unknown): number | undefined {
  if (!isTransient503(ex)) return undefined;

  const header = retryAfterOf(ex)?.trim();
  let delay = DEEPFAKE_DRAIN_RETRY_DEFAULT_MS;

  if (header && /^\d+$/.test(header)) {
    delay = Number(header) * 1000;
  } else if (header && Number.isFinite(Date.parse(header))) {
    delay = Math.max(0, Date.parse(header) - Date.now());
  }

  return Math.min(delay, DEEPFAKE_DRAIN_RETRY_MAX_MS);
}

/**
 * The failure reason an HTTP error from the ai service stands for. A 503 is
 * "no key on this application", unless it is a pod draining (see
 * `drainRetryDelayMs`): that one, once its retries are used up, is "busy,
 * try again shortly". 400/413/415 are the upload itself being refused (a bad
 * or duplicate part, a part over its size limit, a body that is not
 * multipart), i.e. the captured media could not be used; anything without a
 * known status (the network, a client that is not there yet) is a network
 * failure.
 */
export function reasonForHttpError(ex: unknown): DeepfakeFailureReasonEnum {
  switch (statusOf(ex)) {
    case 503:
      return isTransient503(ex)
        ? DeepfakeFailureReasonEnum.rateLimited
        : DeepfakeFailureReasonEnum.notConfigured;
    case 401:
    case 403:
      return DeepfakeFailureReasonEnum.unauthorized;
    case 400:
    case 413:
    case 415:
      return DeepfakeFailureReasonEnum.invalidMedia;
    default:
      return DeepfakeFailureReasonEnum.network;
  }
}

/** The HTTP side of deepfake detection: the ai service's deepfake endpoints. */
@Injectable()
export class DeepfakeApiService {
  private rtc = inject(AuviousRtcService);
  private application = inject(ApplicationService);

  private resource: ApiResource;

  constructor() {
    this.rtc.common$.subscribe((common) => {
      this.resource = common.apiResourceFactory("api/ai");
    });
  }

  /** Whether the ai client exists yet (it comes with the rtc connection). */
  public get isReady(): boolean {
    return !!this.resource;
  }

  private get applicationId() {
    return this.application.getActiveApplication().getId();
  }

  /**
   * Starts a detection task; answers with the ai task id. The body is sent as
   * multipart: axios hands a FormData to the browser untouched, which sets the
   * Content-Type with its boundary — so none is set here.
   */
  public submit(body: FormData): Promise<IDeepfakeSubmitResult> {
    if (!this.resource) {
      return Promise.reject(new Error("The ai client is not ready"));
    }

    return this.resource.create(body, {
      urlPostfix: [this.applicationId, "deepfake", "detection"].join("/"),
      // A 503 here is either "no key on this application" — a state, not a
      // blip — or a pod draining, which `DeepfakeService` retries itself
      // after the `Retry-After` the shared client ignores (see
      // `drainRetryDelayMs`); so no blind 5xx retry here.
      retries: 1
    });
  }

  /**
   * Every check of a conference in this application, newest first: how this
   * page learns about checks after a reload, and about checks another agent
   * started (their events go to that agent only). Agent side only.
   */
  public list(conferenceId: string): Promise<IDeepfakeCheckList> {
    if (!this.resource) {
      return Promise.reject(new Error("The ai client is not ready"));
    }

    return this.resource.get({
      urlPostfix: [this.applicationId, "deepfake"].join("/"),
      params: { conferenceId },
      // polled: a failure is retried on the next tick, not here
      retries: 1
    });
  }

  /** One check's latest state (see `IDeepfakeTaskState`). */
  public getTaskState(taskId: string): Promise<IDeepfakeTaskState> {
    if (!this.resource) {
      return Promise.reject(new Error("The ai client is not ready"));
    }

    return this.resource.get({
      urlPostfix: [this.applicationId, "deepfake", taskId].join("/"),
      retries: 1
    });
  }
}
